import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { openTestCheckout } from "./testCheckout";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { PLATFORM_NAMESPACE } from "../../components/nav";
import { flagEnabled } from "../../config";
import {
  KIND_LABEL,
  MODE_LABEL,
  detectedMode,
  cashfreeAppIdError,
  cashfreeSecretModeError,
  createGatewayAccount,
  getGatewayAssignments,
  listGatewayAccounts,
  razorpayKeyError,
  receiptEmailError,
  RECEIPT_EMAIL_HELP,
  RECEIPT_VERIFY_HELP,
  sendReceiptEmailCode,
  verifyReceiptEmail,
  setGatewayAssignment,
  updateGatewayAccount,
  upiIdError,
  verifyGatewayAccount,
  startTestPayment,
  confirmTestPayment,
  type GatewayAccount,
  type GatewayKind,
  needsReceiptVerify,
} from "../../api/paymentGateway";
import { PasswordField } from "../../components/PasswordField";
import { useResendTimer } from "../../components/useResendTimer";
import { testCodeAccessToken } from "../../api/login";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Payment Gateway (owner 2026-10-24): "I can add multiple gateway setting ...
 * Add New Gateway --> Razorpay or --> Cashfree". An operator configures its
 * own -- its subscribers pay the operator, never Ankh -- and io.ankhos its own
 * for its customers. One account is the default for this namespace (its
 * sites inherit it); an admin may point one subscriber at another.
 */
export function PaymentGatewayScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const [kind, setKind] = useState<GatewayKind | null>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [secretFor, setSecretFor] = useState<GatewayAccount | null>(null);
  const [receiptFor, setReceiptFor] = useState<GatewayAccount | null>(null);
  const [verifyFor, setVerifyFor] = useState<GatewayAccount | null>(null);
  const [override, setOverride] = useState(false);
  const enabled = Boolean(identity.token && ns);

  const accounts = useQuery({ queryKey: ["gatewayaccounts", ns], enabled, queryFn: ({ signal }) => listGatewayAccounts(identity, ns, signal), retry: false });
  const assignments = useQuery({ queryKey: ["gatewayassignments", ns], enabled, queryFn: ({ signal }) => getGatewayAssignments(identity, ns, signal), retry: false });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["gatewayaccounts", ns] });
    void qc.invalidateQueries({ queryKey: ["gatewayassignments", ns] });
  };
  // A save that leaves the receipt email unproved goes straight to its code
  // (owner 2026-10-29: a changed key with the same email was never asked).
  const saved = (a: GatewayAccount) => {
    refresh();
    if (needsReceiptVerify(a)) setVerifyFor(a);
  };
  const verify = useMutation({ mutationFn: (id: string) => verifyGatewayAccount(identity, ns, id), onSettled: refresh });
  const toggle = useMutation({ mutationFn: (a: GatewayAccount) => updateGatewayAccount(identity, ns, a.id, { enabled: !a.enabled }), onSettled: refresh });
  const assign = useMutation({ mutationFn: (v: { sub: string; id: string }) => setGatewayAssignment(identity, ns, v.sub, v.id), onSettled: refresh });
  // Rs 1 test payment (owner 2026-10-26): only a TEST-mode online account;
  // radpayment also refuses outside a test network. Nothing is recorded.
  const [testResult, setTestResult] = useState<{ label: string; paid: boolean | null } | null>(null);
  const testPay = useMutation({
    mutationFn: async (a: GatewayAccount) => {
      setTestResult(null);
      const tp = await startTestPayment(identity, ns, a.id);
      const done = await openTestCheckout(tp, a.label || "Test payment");
      if (!done) return { label: a.label, paid: null };
      return { label: a.label, paid: await confirmTestPayment(identity, ns, a.id, tp.connectorreference, done.paymentId, done.signature) };
    },
    onSuccess: (r) => setTestResult(r),
  });

  const rows = accounts.data ?? [];
  const byId = new Map(rows.map((a) => [a.id, a]));
  const all = assignments.data?.assignments ?? [];
  const defaultId = all.find((a) => a.subscriberaccountid === "")?.gatewayaccountid ?? "";
  const overrides = all.filter((a) => a.subscriberaccountid !== "");
  const effective = assignments.data?.effective ?? null;
  const platform = ns === PLATFORM_NAMESPACE;
  const unproved = rows.filter(needsReceiptVerify);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" component="h1">Payment Gateway</Typography>
          <Typography variant="body2" color="text.secondary">
            {platform
              ? "How AnkhOS's own customers pay their invoices online. Add a gateway account and make one the default."
              : "Where your subscribers pay when they recharge in your customer portal: your own Razorpay account, or your UPI ID for manual payments. With none, the customer portal shows no recharge."}
            {" You may keep a Test mode key and a Live key side by side; the default is the one that takes payments. Test keys work only in a test network."}
          </Typography>
        </Box>
        <Button variant="contained" onClick={(e) => setMenu(e.currentTarget)}>Add new gateway</Button>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          <MenuItem onClick={() => { setKind("razorpay"); setMenu(null); }}>
            <ListItemText primary="Razorpay" secondary="Online: UPI, cards, netbanking" />
          </MenuItem>
          <MenuItem onClick={() => { setKind("upi_qr"); setMenu(null); }}>
            <ListItemText primary="UPI QR (manual)" secondary="Subscribers pay your UPI ID; billing applies it" />
          </MenuItem>
          {/* Owner 2026-10-27 (PG-2): Cashfree is offered to operators only
              with flag payments.cashfree, off on live until its go-live
              checklist is done. io.ankhos always sees it: the owner adds
              Ankh's own live key there. */}
          {(platform || flagEnabled("payments.cashfree")) && (
            <MenuItem onClick={() => { setKind("cashfree"); setMenu(null); }}>
              <ListItemText primary="Cashfree" secondary="Online: UPI, cards, netbanking" />
            </MenuItem>
          )}
        </Menu>
      </Box>

      {(accounts.error || assignments.error) && <Alert severity="error">{errText(accounts.error ?? assignments.error)}</Alert>}
      {testResult && (
        <Alert severity={testResult.paid ? "success" : testResult.paid === false ? "error" : "info"} onClose={() => setTestResult(null)}>
          {testResult.paid
            ? `Test payment of ₹1 through ${testResult.label} went through. Nothing was recorded and no receipt was made.`
            : testResult.paid === false
              ? `The gateway says the ₹1 test payment through ${testResult.label} was not paid.`
              : `Test payment through ${testResult.label} was closed before paying.`}
        </Alert>
      )}
      {[verify.error, toggle.error, assign.error, testPay.error].filter(Boolean).map((e, i) => <Alert key={i} severity="error">{errText(e)}</Alert>)}

      {assignments.data && (effective ? (
        <Alert severity={effective.account.mode === "test" ? "warning" : "success"}>
          Payments here go to <b>{effective.account.label}</b> ({KIND_LABEL[effective.account.connectorname]}, {effective.account.keyid})
          {effective.assignedat !== ns ? `, set at ${effective.assignedat}` : ""}.
          {effective.account.mode === "test" && " This is a TEST key: no real money moves."}
        </Alert>
      ) : (
        <Alert severity="info">No gateway is set for payments here yet{assignments.data.effectiveerror ? `: ${assignments.data.effectiveerror}` : ""}. Add one and make it the default.</Alert>
      ))}

      {accounts.data && rows.length === 0 && <Alert severity="info">No gateway account yet.</Alert>}
      {unproved.length > 0 && (
        <Alert severity="warning" data-testid="receipt-unverified-banner">
          Receipt email not verified: {RECEIPT_VERIFY_HELP}
          {unproved.map((a) => (
            <Box key={a.id} sx={{ mt: 0.5 }}>
              {a.label} ({a.receipt_email}){" "}
              <Button size="small" variant="outlined" color="warning" onClick={() => setVerifyFor(a)}>Verify</Button>
            </Box>
          ))}
        </Alert>
      )}
      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Key ID / UPI ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    {a.label} {a.id === defaultId && <Chip size="small" color="primary" label="Default" sx={{ ml: 1 }} />}
                    {a.connectorname !== "upi_qr" && (a.receipt_email ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Receipts: {a.receipt_email}{" "}
                        {a.receipt_email_verified ? (
                          <Chip size="small" color="success" variant="outlined" label="Verified" data-testid={`receipt-verified-${a.id}`} />
                        ) : (
                          <Button size="small" color="warning" sx={{ minWidth: 0, py: 0 }} title={RECEIPT_VERIFY_HELP} onClick={() => setVerifyFor(a)}
                            data-testid={`receipt-verify-${a.id}`}>
                            Not verified: verify
                          </Button>
                        )}
                      </Typography>
                    ) : (
                      <Button size="small" color="warning" sx={{ display: "block", px: 0 }} title={RECEIPT_EMAIL_HELP} onClick={() => setReceiptFor(a)}>
                        Set a receipt email
                      </Button>
                    ))}
                  </TableCell>
                  <TableCell>
                    {KIND_LABEL[a.connectorname] ?? a.connectorname}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={a.mode === "live" ? "success" : "warning"} label={MODE_LABEL[a.mode] ?? a.mode} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{a.keyid}</TableCell>
                  <TableCell>
                    {!a.enabled ? <Chip size="small" label="Disabled" /> : <Chip size="small" color={a.status === "Verified" ? "success" : a.status === "Failed" ? "error" : "default"} label={a.status} />}
                    {a.lasterror && <Typography variant="caption" color="error" display="block">{a.lasterror}</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Button size="small" disabled={verify.isPending || !a.enabled} onClick={() => verify.mutate(a.id)}>Check</Button>
                    {a.mode === "test" && a.connectorname !== "upi_qr" && (
                      <Button size="small" disabled={testPay.isPending || !a.enabled} onClick={() => testPay.mutate(a)}
                        title="Pay ₹1 through this test-mode account with the gateway's test card or UPI. No real money; nothing is recorded.">
                        Test payment ₹1
                      </Button>
                    )}
                    {a.id !== defaultId && <Button size="small" disabled={assign.isPending || !a.enabled} onClick={() => assign.mutate({ sub: "", id: a.id })}>Make default</Button>}
                    {a.connectorname !== "upi_qr" && <Button size="small" onClick={() => setSecretFor(a)}>Change keys</Button>}
                    {a.connectorname !== "upi_qr" && a.receipt_email && <Button size="small" onClick={() => setReceiptFor(a)}>Receipt email</Button>}
                    <Button size="small" color={a.enabled ? "error" : "primary"} disabled={toggle.isPending} onClick={() => toggle.mutate(a)}>{a.enabled ? "Disable" : "Enable"}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {defaultId && (
        <Box>
          <Button size="small" color="error" disabled={assign.isPending} onClick={() => assign.mutate({ sub: "", id: "" })}>Remove the default</Button>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">One subscriber pays elsewhere</Typography>
          <Typography variant="body2" color="text.secondary">Admins only: point one subscriber account at another gateway account than the default.</Typography>
        </Box>
        <Button variant="outlined" disabled={rows.length === 0} onClick={() => setOverride(true)}>Add</Button>
      </Box>
      {overrides.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Subscriber account</TableCell>
                <TableCell>Pays into</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {overrides.map((o) => (
                <TableRow key={o.subscriberaccountid}>
                  <TableCell sx={{ fontFamily: "monospace" }}>{o.subscriberaccountid}</TableCell>
                  <TableCell>{byId.get(o.gatewayaccountid)?.label ?? o.gatewayaccountid}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" disabled={assign.isPending} onClick={() => assign.mutate({ sub: o.subscriberaccountid, id: "" })}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {kind && <AddGatewayDialog ns={ns} kind={kind} onClose={() => setKind(null)} onDone={(a) => { setKind(null); saved(a); }} />}
      {verifyFor && <ReceiptVerifyDialog ns={ns} account={verifyFor} onClose={() => setVerifyFor(null)} onDone={() => { refresh(); setVerifyFor(null); }} />}
      {receiptFor && <ReceiptEmailDialog ns={ns} account={receiptFor} onClose={() => setReceiptFor(null)} onDone={(a) => { setReceiptFor(null); saved(a); }} />}
      {secretFor && <ReplaceSecretDialog ns={ns} account={secretFor} onClose={() => setSecretFor(null)} onDone={(a) => { setSecretFor(null); saved(a); }} />}
      {override && <OverrideDialog accounts={rows.filter((a) => a.enabled)} onClose={() => setOverride(false)} onSave={(sub, id) => { assign.mutate({ sub, id }); setOverride(false); }} />}
    </Box>
  );
}

function AddGatewayDialog({ ns, kind, onClose, onDone }: { ns: string; kind: GatewayKind; onClose: () => void; onDone: (a: GatewayAccount) => void }) {
  const identity = useIdentity();
  const upi = kind === "upi_qr";
  const cashfree = kind === "cashfree";
  const [label, setLabel] = useState("");
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [webhook, setWebhook] = useState("");
  const [receipt, setReceipt] = useState("");
  const receiptErr = upi ? null : receiptEmailError(receipt);
  // Cashfree's App ID does not say test or live, so the operator says it.
  const [cfMode, setCfMode] = useState<"test" | "live">("test");
  const keyErr = upi ? upiIdError(keyId) : cashfree ? cashfreeAppIdError(keyId) : razorpayKeyError(keyId);
  const secretErr = cashfree ? cashfreeSecretModeError(secret, cfMode) : null;
  const mode = cashfree ? cfMode : detectedMode(kind, keyId);
  const ready = label.trim() !== "" && !keyErr && !secretErr && !receiptErr && (upi || secret.trim() !== "");
  const save = useMutation({
    mutationFn: async () => {
      const a = await createGatewayAccount(identity, ns, { kind, label, keyId, keySecret: secret, webhookSecret: webhook, receiptEmail: receipt, ...(cashfree ? { mode: cfMode } : {}) });
      // A Razorpay or Cashfree key is checked at once with one harmless call; a UPI ID has nothing to check.
      return upi ? a : verifyGatewayAccount(identity, ns, a.id);
    },
    onSuccess: onDone,
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add {KIND_LABEL[kind]}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={upi ? "Payee name (shown to subscribers)" : "Name"} value={label} onChange={(e) => setLabel(e.target.value)} fullWidth />
          <TextField
            label={upi ? "UPI ID" : cashfree ? "App ID" : "Key ID"}
            value={keyId}
            onChange={(e) => setKeyId(e.target.value)}
            error={keyId !== "" && Boolean(keyErr)}
            helperText={
              keyId !== ""
                ? keyErr ?? " "
                : upi
                  ? "For example yourname@okicici"
                  : cashfree
                    ? "From Cashfree Merchant Dashboard › Payment Gateway › Developers › API Keys"
                    : "From Razorpay Dashboard › Account & Settings › API keys"
            }
            autoComplete="off"
            fullWidth
          />
          {!upi && (
            <>
              {cashfree && (
                <TextField select label="Mode" value={cfMode} onChange={(e) => setCfMode(e.target.value as "test" | "live")} fullWidth
                  helperText="Cashfree's App ID does not say whether it is a Test or a Live key: choose the environment you copied it from.">
                  <MenuItem value="test">Test mode (Cashfree sandbox)</MenuItem>
                  <MenuItem value="live">Live</MenuItem>
                </TextField>
              )}
              <PasswordField
                label={cashfree ? "Secret key" : "Key secret"}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
                fullWidth
                error={Boolean(secretErr)}
                helperText={secretErr ?? "Stored encrypted. It is never shown again."}
              />
              <ReceiptEmailField value={receipt} onChange={setReceipt} />
              {!cashfree && (
                <PasswordField label="Webhook secret (optional)" value={webhook} onChange={(e) => setWebhook(e.target.value)} autoComplete="new-password" fullWidth />
              )}
              {!keyErr && mode === "test" && (
                <Alert severity="warning">
                  {cashfree ? "Selected" : "Detected"}: <b>Test mode</b>. Payments use {cashfree ? "Cashfree" : "Razorpay"} test cards and UPI and move no real money. A test key works only in a test network (such as testisp); in a live network it is refused.
                </Alert>
              )}
              {!keyErr && mode === "live" && (
                <Alert severity="success">{cashfree ? "Selected" : "Detected"}: <b>Live</b>. Subscribers pay real money into this account.</Alert>
              )}
            </>
          )}
          {upi && (
            <Alert severity="info">
              Subscribers see a QR for this UPI ID with the plan's price, pay from any UPI app, then send you the UPI reference. Billing checks your bank and applies it under Billing › UPI payment notices.
            </Alert>
          )}
          {save.error && <Alert severity="error">{errText(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ready || save.isPending} onClick={() => save.mutate()}>{upi ? "Save" : "Save and check"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ReplaceSecretDialog({ ns, account, onClose, onDone }: { ns: string; account: GatewayAccount; onClose: () => void; onDone: (a: GatewayAccount) => void }) {
  const identity = useIdentity();
  // A gateway issues a new key ID and secret together (owner 2026-10-28):
  // both change here; an unchanged key ID replaces the secret alone. New keys
  // of the other mode switch the account test <-> live in place.
  const cashfree = account.connectorname === "cashfree";
  const [keyId, setKeyId] = useState(account.keyid);
  const [secret, setSecret] = useState("");
  const [cfMode, setCfMode] = useState<"test" | "live">(account.mode);
  const [webhook, setWebhook] = useState("");
  const [receipt, setReceipt] = useState(account.receipt_email ?? "");
  const receiptErr = receiptEmailError(receipt);
  const newKey = keyId.trim() !== account.keyid;
  const mode = !newKey ? account.mode : cashfree ? cfMode : detectedMode(account.connectorname, keyId);
  const switching = mode !== null && mode !== account.mode;
  const keyErr = newKey && mode === null ? "A Razorpay key ID starts with rzp_test_ or rzp_live_" : null;
  const secretErr = cashfree && mode ? cashfreeSecretModeError(secret, mode) : null;
  const save = useMutation({
    mutationFn: async () => {
      await updateGatewayAccount(identity, ns, account.id, {
        ...(newKey ? { keyId } : {}),
        ...(newKey && cashfree ? { mode: cfMode } : {}),
        keySecret: secret,
        receiptEmail: receipt,
        ...(switching && !cashfree && webhook.trim() !== "" ? { webhookSecret: webhook } : {}),
      });
      return verifyGatewayAccount(identity, ns, account.id);
    },
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Change the keys of {account.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Key ID" value={keyId} onChange={(e) => setKeyId(e.target.value)} autoComplete="off" fullWidth error={Boolean(keyErr)}
            helperText={keyErr ?? (newKey ? "New key ID: enter the secret issued with it." : "Paste the new key ID from the gateway, or keep it to replace only the secret.")} />
          {cashfree && newKey && (
            <TextField select label="Mode" value={cfMode} onChange={(e) => setCfMode(e.target.value as "test" | "live")} fullWidth
              helperText="Cashfree's App ID does not say whether it is a Test or a Live key: choose the environment you copied it from.">
              <MenuItem value="test">Test mode (Cashfree sandbox)</MenuItem>
              <MenuItem value="live">Live</MenuItem>
            </TextField>
          )}
          <PasswordField label="New key secret" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" fullWidth
            error={Boolean(secretErr)} helperText={secretErr ?? " "} />
          <ReceiptEmailField value={receipt} onChange={setReceipt} />
          {switching && !cashfree && (
            <PasswordField label="Webhook secret for the new keys (optional)" value={webhook} onChange={(e) => setWebhook(e.target.value)} autoComplete="new-password" fullWidth
              helperText="The old webhook secret belongs to the old mode and is removed." />
          )}
          {switching && mode === "live" && (
            <Alert severity="warning">This account switches to Live: from now on it takes real payments, with real money.</Alert>
          )}
          {switching && mode === "test" && (
            <Alert severity="warning">This account switches to Test mode: subscribers can no longer pay real money through it.</Alert>
          )}
          <Alert severity="info">
            {switching
              ? "A switch is refused while a payment started in the current mode is still pending (up to 48 hours)."
              : "Payments started before the change and still unpaid may not be confirmed with the new keys. Change keys when no subscriber is paying."}
          </Alert>
          {save.error && <Alert severity="error">{errText(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={secret.trim() === "" || keyId.trim() === "" || Boolean(keyErr) || Boolean(secretErr) || Boolean(receiptErr) || save.isPending} onClick={() => save.mutate()}>Save and check</Button>
      </DialogActions>
    </Dialog>
  );
}

/** The receipt email field (owner 2026-10-29), with its required-and-why help. */
function ReceiptEmailField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const err = value.trim() === "" ? null : receiptEmailError(value);
  return (
    <TextField label="Receipt email" type="email" value={value} onChange={(e) => onChange(e.target.value)} autoComplete="email" fullWidth required
      error={Boolean(err)} helperText={err ?? RECEIPT_EMAIL_HELP} />
  );
}

/** Prove the receipt email with a code sent to it (owner 2026-10-29). */
function ReceiptVerifyDialog({ ns, account, onClose, onDone }: { ns: string; account: GatewayAccount; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const timer = useResendTimer();
  const [requestId, setRequestId] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const send = useMutation({
    mutationFn: () => sendReceiptEmailCode(identity, ns, account.id),
    onSuccess: (c) => {
      if (c.status === "already_verified") {
        onDone();
        return;
      }
      setRequestId(c.requestId ?? "");
      setSentTo(c.email ?? "");
      setCode("");
      timer.start(c.resendIn ?? 30);
    },
  });
  const check = useMutation({
    mutationFn: () => verifyReceiptEmail(identity, ns, account.id, testCodeAccessToken(requestId, code)),
    onSuccess: onDone,
  });
  const err = send.error ?? check.error;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Verify the receipt email for {account.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            We email a 6-digit code to <b>{account.receipt_email}</b>. {RECEIPT_VERIFY_HELP}
          </Typography>
          <Box>
            <Button variant={requestId ? "text" : "contained"} disabled={send.isPending || timer.left > 0} onClick={() => send.mutate()}>
              {timer.left > 0 ? `Resend code in ${timer.left}s` : requestId ? "Resend code" : "Send code"}
            </Button>
          </Box>
          {requestId && (
            <TextField label="Code from the email" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", "data-testid": "receipt-code" }} helperText={sentTo ? `Sent to ${sentTo}. Valid for 10 minutes.` : undefined} autoFocus />
          )}
          {err && <Alert severity="error">{errText(err)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!requestId || code.length !== 6 || check.isPending} onClick={() => check.mutate()}>Verify</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Set or change only the receipt email of a Razorpay or Cashfree account. */
function ReceiptEmailDialog({ ns, account, onClose, onDone }: { ns: string; account: GatewayAccount; onClose: () => void; onDone: (a: GatewayAccount) => void }) {
  const identity = useIdentity();
  const [receipt, setReceipt] = useState(account.receipt_email ?? "");
  const save = useMutation({
    mutationFn: () => updateGatewayAccount(identity, ns, account.id, { receiptEmail: receipt }),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{account.receipt_email ? "Receipt email" : "Set a receipt email"} for {account.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ReceiptEmailField value={receipt} onChange={setReceipt} />
          {save.error && <Alert severity="error">{errText(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(receiptEmailError(receipt)) || save.isPending} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function OverrideDialog({ accounts, onClose, onSave }: { accounts: GatewayAccount[]; onClose: () => void; onSave: (sub: string, id: string) => void }) {
  const [sub, setSub] = useState("");
  const [id, setId] = useState(accounts[0]?.id ?? "");
  const subErr = sub.trim() === "" || UUID.test(sub.trim()) ? null : "A subscriber account ID (from Customer lookup)";
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>One subscriber pays elsewhere</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Subscriber account ID" value={sub} onChange={(e) => setSub(e.target.value)} error={Boolean(subErr)} helperText={subErr ?? " "} fullWidth />
          <TextField select label="Pays into" value={id} onChange={(e) => setId(e.target.value)} fullWidth>
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.label} ({KIND_LABEL[a.connectorname]})</MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!UUID.test(sub.trim()) || !id} onClick={() => onSave(sub.trim(), id)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
